'use client';

import { type MouseEvent, useEffect, useRef, useState } from 'react';

const HOVER_EXIT_DELAY_MS = 150;

/** Click regions for interactive mode */
type ClickRegion = 'ear' | 'head' | 'body' | 'hand';

export interface UseRainaHoverOptions {
  /** Enable hover detection */
  interactive: boolean;
  /** Container ref to stop DOM walk */
  containerRef: React.RefObject<HTMLDivElement | null>;
}

export interface UseRainaHoverResult {
  /** Whether user is hovering over an interactive region */
  isHovering: boolean;
  /** Mouse enter handler */
  onMouseEnter: (e: MouseEvent<HTMLDivElement>) => void;
  /** Mouse move handler */
  onMouseMove: (e: MouseEvent<HTMLDivElement>) => void;
  /** Mouse leave handler */
  onMouseLeave: () => void;
}

/**
 * Hook for managing Raina's hover state detection.
 *
 * Uses RAF throttling and DOM tree walking (O(log n)) for efficient
 * hover detection over interactive regions.
 */
export function useRainaHover({ interactive, containerRef }: UseRainaHoverOptions): UseRainaHoverResult {
  const [isHovering, setIsHovering] = useState(false);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hoverRafIdRef = useRef<number | null>(null);

  // Walk up DOM tree to find the closest parent with a data-region attribute
  const getRegionFromTarget = (target: EventTarget | null): ClickRegion | null => {
    let element = target as Element | null;
    while (element && element !== containerRef.current) {
      if (element instanceof Element) {
        const region = element.getAttribute('data-region') as ClickRegion | null;
        if (region) return region;
      }
      element = element.parentElement;
    }
    return null;
  };

  const updateHoverFromTarget = (e: MouseEvent<HTMLDivElement>) => {
    if (!interactive) return;

    // If frame is already scheduled, skip
    if (hoverRafIdRef.current !== null) return;

    hoverRafIdRef.current = requestAnimationFrame(() => {
      hoverRafIdRef.current = null;
      if (!interactive) return;

      const region = getRegionFromTarget(e.target);

      if (region) {
        if (hoverTimeoutRef.current) {
          clearTimeout(hoverTimeoutRef.current);
          hoverTimeoutRef.current = null;
        }
        setIsHovering(true);
        return;
      }

      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = setTimeout(() => {
        setIsHovering(false);
        hoverTimeoutRef.current = null;
      }, HOVER_EXIT_DELAY_MS);
    });
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
      if (hoverRafIdRef.current) cancelAnimationFrame(hoverRafIdRef.current);
    };
  }, []);

  return {
    isHovering,
    onMouseEnter: updateHoverFromTarget,
    onMouseMove: updateHoverFromTarget,
    onMouseLeave: handleMouseLeave,
  };
}
